/**
 * BaaS Console EFX / Crossborder history from live account statements
 * (FX/EFX + international rails), via MobileService console proxy.
 */

import { fetchBaasAccounts, isValutSegregationWalletInstance } from "./baasAccountsListApi";
import { fetchBaasTransactions } from "./baasTransactionsApi";

const ACTIVE_ACCOUNT_TYPES = new Set(["operational", "settlement", "reserve"]);

const FX_RAILS = new Set(["FX", "EFX", "WIRE", "ACH", "SWIFT", "SEPA"]);

const isFxOrCrossborderTxn = (txn) => {
  const type = String(txn?.type || txn?.rail || "").toUpperCase();
  if (FX_RAILS.has(type)) return true;
  const haystack = `${txn?.description || ""} ${txn?.referenceId || ""}`.toUpperCase();
  return (
    haystack.includes("FX CONVERSION") ||
    haystack.includes("FXCONV") ||
    haystack.includes("EFX") ||
    /\bFX\b/.test(haystack)
  );
};

const formatCrossborderTime = (iso) => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return String(iso);
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
    .format(date)
    .replace(",", "");
};

const mapTxnToCrossborderHistory = (txn) => {
  const currency = String(txn?.currency || "BRL").toLowerCase();
  const amountMajor = Math.abs(Number(txn?.amount ?? 0)) / 100;
  const outbound = txn?.direction === "outbound";
  const type = outbound ? "payout" : "payin";

  return {
    id: txn.id,
    referenceId: txn.referenceId || txn.id,
    type,
    time: formatCrossborderTime(txn.createdAt),
    senderCustomer: txn.entityName || txn.clientName || txn.accountAlias || "—",
    originRailId: String(txn.type || "fx").toLowerCase(),
    destinationRailId: String(txn.type || "fx").toLowerCase(),
    fromAmount: outbound ? amountMajor : amountMajor,
    fromCurrency: currency,
    toAmount: amountMajor,
    toCurrency: currency,
    valutAccount: txn.accountAlias || txn.accountId || "—",
    beneficiaryAccount: txn.counterpartyName || txn.description || "—",
    senderAmount: amountMajor,
    senderAmountCurrency: currency,
    receiverAmount: amountMajor,
    receiverAmountCurrency: currency,
    operationFee: 0,
    operationFeeCurrency: currency,
    status: txn.status === "failed" ? "failed" : "completed",
    accountId: txn.accountId ?? null,
    accountLabel: txn.accountAlias
      ? `${txn.accountAlias}${txn.accountNumber ? ` (${txn.accountNumber})` : ""}`
      : null,
    accountNumber: txn.accountNumber ?? null,
    entityName: txn.entityName ?? null,
    description: txn.description || "",
    raw: txn,
  };
};

/**
 * Live Crossborder/EFX history for the selected BaaS instance.
 * When `aggregatePeers` is true (matriz + todas as instâncias), uses peer-statements.
 *
 * @param {object} instance
 * @param {{ aggregatePeers?: boolean, startDate?: Date, endDate?: Date, instanceId?: number|null }} [options]
 */
export const fetchBaasCrossborderHistory = async (instance, options = {}) => {
  if (!instance) return [];

  const usePeerLedger =
    options.aggregatePeers === true && isValutSegregationWalletInstance(instance);

  const transactions = usePeerLedger
    ? await fetchBaasTransactions(instance, {
        includePix: false,
        fetchAll: true,
        startDate: options.startDate,
        endDate: options.endDate,
        instanceId: options.instanceId,
      }).catch(() => [])
    : await (async () => {
        const accounts = await fetchBaasAccounts(instance).catch(() => []);
        return fetchBaasTransactions(instance, {
          accounts,
          includePix: false,
        }).catch(() => []);
      })();

  return transactions
    .filter(isFxOrCrossborderTxn)
    .map(mapTxnToCrossborderHistory)
    .sort((a, b) => String(b.raw?.createdAt || "").localeCompare(String(a.raw?.createdAt || "")));
};

/** @deprecated Prefer fetchBaasCrossborderHistory */
export const getMockBaasCrossborderHistory = () => [];

export const getBaasCrossborderAccountOptions = async (instance) => {
  if (!instance) return [];
  const accounts = await fetchBaasAccounts(instance).catch(() => []);
  return accounts
    .filter(
      (account) =>
        account.status === "active" && ACTIVE_ACCOUNT_TYPES.has(account.type),
    )
    .map((account) => ({
      id: account.id,
      label: `${account.alias} · ${account.accountNumber}`,
      entityName: account.entityName,
    }));
};
